export const DEFAULT_FORM = {
  destinationURL: 'https://www.favordating.com/',
  campaignSource: '',
  campaignName: '',
  remarks: '',
  isActive: true,
};

export const ITEMS_PER_PAGE = 10;

export const SOURCE_OPTIONS = [
  { value: 'google', label: 'Google' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'snapchat', label: 'Snapchat' },
  { value: 'reddit', label: 'Reddit' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'telegram', label: 'Telegram' },
];

export const normalizeSourceValue = (source) => String(source || '').trim().toLowerCase();

export const normalizeSourceLabel = (source) => String(source || '').trim();

export const getSourceLabel = (source) => {
  const normalizedSource = normalizeSourceValue(source);
  const matched = SOURCE_OPTIONS.find((item) => item.value === normalizedSource);
  return matched?.label || normalizeSourceLabel(source) || '-';
};

export const getSourceOptionList = () => SOURCE_OPTIONS.map((item) => ({ ...item }));

export const isApprovedSource = (source) =>
  SOURCE_OPTIONS.some((item) => item.value === normalizeSourceValue(source));

export const getRowsFromResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.rows)) return response.rows;
  if (Array.isArray(response?.list)) return response.list;
  return [];
};

export const normalizeCampaignRow = (row, index) => {
  const id =
    row?.id ||
    row?._id ||
    row?.campaignId ||
    row?.utmId ||
    row?.uuid ||
    `${row?.campaignName || 'campaign'}-${index}`;

  return {
    ...row,
    id,
    destinationURL: row?.destinationURL || row?.destinationUrl || row?.url || '',
    campaignSource: normalizeSourceLabel(row?.campaignSource || row?.source),
    campaignName: row?.campaignName || row?.name || '',
    remarks: row?.remarks || '',
    cost: row?.cost ?? '',
    isActive: typeof row?.isActive === 'boolean' ? row.isActive : row?.status === 'active',
    createdAt: row?.createdAt || row?.created_at || row?.date || row?.updatedAt || '',
  };
};

export const formatDate = (value) => {
  if (!value) return '-';
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return value;

  return parsedDate.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const getStatusLabel = (isActive) => (isActive ? 'Active' : 'Paused');

export const buildCampaignLink = (record) => {
  const destinationURL = record?.destinationURL || record?.destinationUrl || record?.url || '';
  const campaignSource = normalizeSourceLabel(record?.campaignSource || record?.sourceValue || record?.source);
  const campaignName = String(record?.campaignName || record?.name || '').trim();

  if (!destinationURL || !campaignSource || !campaignName) return record?.utmlink || destinationURL || '-';

  try {
    const url = new URL(destinationURL);
    url.searchParams.set('utm_source', campaignSource);
    url.searchParams.set('utm_campaign', campaignName);
    return url.toString();
  } catch (error) {
    const separator = destinationURL.includes('?') ? '&' : '?';
    return `${destinationURL}${separator}utm_source=${encodeURIComponent(campaignSource)}&utm_campaign=${encodeURIComponent(campaignName)}`;
  }
};

export const getCampaignIdLabel = (record, index = 0) => {
  const candidate = record?._id || '';
  if (candidate) return String(candidate).slice(-8).toUpperCase();
  return `CMP${String(index + 1).padStart(3, '0')}`;
};

export const mapCampaignToTableRow = (record, index = 0) => ({
  ...record,
  utmId: getCampaignIdLabel(record, index),
  name: record?.campaignName || '-',
  date: formatDate(record?.createdAt),
  utmlink: buildCampaignLink(record),
  cost: record?.cost || '',
  source: getSourceLabel(record?.campaignSource),
  sourceValue: record?.campaignSource || '',
  status: getStatusLabel(record?.isActive),
  clicks: record?.clicks || 0,
  viewed: record?.viewed || 0,
  signups: record?.signups || 0,
  profileCompleted: record?.profileCompleted || 0,
  ghostUsers: record?.ghostUsers || 0,
  webuser: record?.webuser || 0,
  ghostwebusers: record?.ghostwebusers || 0,
  appusers: record?.appusers || 0,
  ghostappusers: record?.ghostappusers || 0,
  subscribed: record?.subscribed || 0,
  revenue: record?.revenue || 0,
  cacsubscribed: record?.cacsubscribed || 0,
  cacprofilecompleted: record?.cacprofilecompleted || 0,
  roas: record?.roas || 0,
  remarks: record?.remarks || '-',
});

export const getStatusColor = (isActive) => ({
  color: isActive ? '#027a48' : '#b42318',
  backgroundColor: isActive ? '#ecfdf3' : '#fef3f2',
  border: `1px solid ${isActive ? '#abefc6' : '#fecdca'}`,
  borderRadius: '999px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '84px',
  padding: '4px 10px',
  fontSize: '12px',
  fontWeight: 600,
});

export const getFormPayload = (formData) => ({
  destinationURL: formData.destinationURL.trim(),
  campaignSource: normalizeSourceLabel(formData.campaignSource),
  campaignName: formData.campaignName.trim(),
  remarks: formData.remarks.trim(),
  isActive: Boolean(formData.isActive),
});

export const validateCampaignForm = (values) => {
  const nextErrors = {};

  if (!values.destinationURL.trim()) {
    nextErrors.destinationURL = 'Destination URL is required';
  } else {
    try {
      new URL(values.destinationURL.trim());
    } catch (error) {
      nextErrors.destinationURL = 'Enter a valid URL';
    }
  }

  if (!values.campaignSource.trim()) {
    nextErrors.campaignSource = 'Campaign source is required';
  } else if (!isApprovedSource(values.campaignSource)) {
    nextErrors.campaignSource = 'Select a valid campaign source';
  }

  const campaignName = values.campaignName.trim();
  if (!campaignName) {
    nextErrors.campaignName = 'Campaign name is required';
  } else if (/^\d+$/.test(campaignName)) {
    nextErrors.campaignName = 'Campaign name cannot be only numbers';
  }

  return nextErrors;
};

export const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value || '-';
  return `₹${amount.toLocaleString('en-IN', {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatRoas = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value || '-';
  return `${amount.toFixed(2)}x`;
};

export const filterCampaignRows = ({ rows, searchText, sourceFilter, statusFilter }) => {
  const query = searchText.trim().toLowerCase();

  return rows.filter((item) => {
    const matchesSearch =
      !query ||
      [
        item.campaignName,
        getSourceLabel(item.campaignSource),
        item.destinationURL,
        item.remarks,
        getStatusLabel(item.isActive),
      ].some((value) => value?.toLowerCase().includes(query));

    const matchesSource = !sourceFilter || getSourceLabel(item.campaignSource) === sourceFilter;
    const matchesStatus =
      statusFilter === ''
        ? true
        : statusFilter === 'active'
          ? item.isActive
          : !item.isActive;

    return matchesSearch && matchesSource && matchesStatus;
  });
};

export const getCampaignStats = (rows) => {
  const totalEntries = rows.length;
  const activeEntries = rows.filter((item) => item.isActive).length;
  const inactiveEntries = totalEntries - activeEntries;

  return [
    { subTitle: 'Total Campaigns', title: totalEntries, filterKey: '' },
    { subTitle: 'Active Campaigns', title: activeEntries, filterKey: 'active' },
    { subTitle: 'Paused Campaigns', title: inactiveEntries, filterKey: 'paused' },
  ];
};
